import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Flex,
  Loader,
  Text,
  IconButton,
} from '@vibe/core';
import { Add, CloseSmall } from '@vibe/icons';
import {
  getPermissionRules,
  createPermissionRule,
  createPermissionRulesBulk,
  deletePermissionRule,
  type PermissionRule,
} from '../../api/permission.api';
import { type MondayUser } from '../../api/monday.api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Constants ──────────────────────────────────────────────────────────────────

const SEARCH_MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const USERS_BATCH_SIZE = 1000;
const MAX_DROPDOWN_RESULTS = 20;

const USERS_PAGE_QUERY = `
  query GetUsers($limit: Int!, $page: Int!) {
    users(kind: non_guests, limit: $limit, page: $page) {
      id
      name
      email
      photo_thumb_small
      title
      teams {
        id
        name
      }
    }
  }
`;

// ─── Types ──────────────────────────────────────────────────────────────────────

type DropdownOptionObj = { value: string; label: string };

interface ApproverEntry {
  granteeType: 'user' | 'team';
  granteeId: string;
}

interface ViewerEntry {
  ruleId: string;
  userId: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

async function searchMondayUsers(query: string): Promise<MondayUser[]> {
  const matched: MondayUser[] = [];
  const lowerQ = query.toLowerCase();
  let page = 1;

  while (page <= 2) {
    const response = await monday.api(USERS_PAGE_QUERY, {
      variables: { limit: USERS_BATCH_SIZE, page },
    });

    const data = response.data as {
      users?: Array<{
        id: number | string;
        name: string;
        email: string;
        photo_thumb_small: string | null;
        title: string | null;
        teams?: Array<{ id: number | string; name: string }>;
      }>;
    };

    const users = data.users ?? [];
    if (users.length === 0) break;

    for (const u of users) {
      if (
        u.name.toLowerCase().includes(lowerQ) ||
        u.email.toLowerCase().includes(lowerQ)
      ) {
        matched.push({
          id: Number(u.id),
          name: u.name,
          email: u.email,
          photo_thumb_small: u.photo_thumb_small ?? null,
          title: u.title ?? null,
          teams: (u.teams ?? []).map((t) => ({ id: Number(t.id), name: t.name })),
        });
      }
      if (matched.length >= MAX_DROPDOWN_RESULTS) break;
    }

    if (matched.length >= MAX_DROPDOWN_RESULTS || users.length < USERS_BATCH_SIZE) break;
    page++;
  }

  return matched;
}

async function fetchUsersByIds(userIds: string[]): Promise<MondayUser[]> {
  if (userIds.length === 0) return [];

  const needed = new Set(userIds);
  const found: MondayUser[] = [];
  let page = 1;

  while (found.length < needed.size && page <= 5) {
    const response = await monday.api(USERS_PAGE_QUERY, {
      variables: { limit: USERS_BATCH_SIZE, page },
    });

    const data = response.data as {
      users?: Array<{
        id: number | string;
        name: string;
        email: string;
        photo_thumb_small: string | null;
        title: string | null;
        teams?: Array<{ id: number | string; name: string }>;
      }>;
    };

    const users = data.users ?? [];
    if (users.length === 0) break;

    for (const u of users) {
      if (needed.has(String(u.id))) {
        found.push({
          id: Number(u.id),
          name: u.name,
          email: u.email,
          photo_thumb_small: u.photo_thumb_small ?? null,
          title: u.title ?? null,
          teams: (u.teams ?? []).map((t) => ({ id: Number(t.id), name: t.name })),
        });
      }
    }

    if (users.length < USERS_BATCH_SIZE) break;
    page++;
  }

  return found;
}

// ─── Component ──────────────────────────────────────────────────────────────────

const TimesheetViewersTab: React.FC = () => {
  // ─── State ─────────────────────────────────────────────────────────────────
  const [approveRules, setApproveRules] = useState<PermissionRule[]>([]);
  const [viewerRules, setViewerRules] = useState<PermissionRule[]>([]);
  const [userCache, setUserCache] = useState<Map<string, MondayUser>>(new Map());
  const [teamCache, setTeamCache] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add viewer form
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedViewers, setSelectedViewers] = useState<DropdownOptionObj[]>([]);
  const [isAddSaving, setIsAddSaving] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  // User search
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<MondayUser[]>([]);
  const [isUserSearching, setIsUserSearching] = useState(false);
  const debouncedUserSearch = useDebouncedValue(userSearchQuery, DEBOUNCE_MS);

  // ─── Load data ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [approveData, viewerData] = await Promise.all([
        getPermissionRules('timesheet_approve'),
        getPermissionRules('timesheet_view'),
      ]);

      setApproveRules(approveData);
      setViewerRules(viewerData);

      // Collect all user IDs referenced in both rule sets
      const referencedUserIds = new Set<string>();
      for (const r of approveData) {
        if (r.granteeType === 'user') referencedUserIds.add(r.granteeId);
        if (r.targetType === 'user' && r.targetId) referencedUserIds.add(r.targetId);
      }
      for (const r of viewerData) {
        if (r.granteeType === 'user') referencedUserIds.add(r.granteeId);
      }

      if (referencedUserIds.size > 0) {
        const users = await fetchUsersByIds(Array.from(referencedUserIds));
        const uCache = new Map<string, MondayUser>();
        const tCache = new Map<string, string>();
        for (const u of users) {
          uCache.set(String(u.id), u);
          for (const t of u.teams) {
            tCache.set(String(t.id), t.name);
          }
        }
        setUserCache(uCache);
        setTeamCache(tCache);
      }

      // Also load first batch of users to extract teams for team name resolution
      const firstBatch = await monday.api(USERS_PAGE_QUERY, {
        variables: { limit: USERS_BATCH_SIZE, page: 1 },
      });
      const firstData = firstBatch.data as {
        users?: Array<{
          id: number | string;
          teams?: Array<{ id: number | string; name: string }>;
        }>;
      };
      const teamsMap = new Map<string, string>();
      for (const u of firstData.users ?? []) {
        for (const t of u.teams ?? []) {
          teamsMap.set(String(t.id), t.name);
        }
      }
      setTeamCache((prev) => {
        const next = new Map(prev);
        for (const [id, name] of teamsMap) next.set(id, name);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── User search effect ────────────────────────────────────────────────────
  useEffect(() => {
    if (!debouncedUserSearch || debouncedUserSearch.length < SEARCH_MIN_CHARS) {
      setUserSearchResults([]);
      return;
    }
    let cancelled = false;
    setIsUserSearching(true);
    searchMondayUsers(debouncedUserSearch)
      .then((results) => {
        if (!cancelled) setUserSearchResults(results);
      })
      .catch(() => { /* silently fail */ })
      .finally(() => {
        if (!cancelled) setIsUserSearching(false);
      });
    return () => { cancelled = true; };
  }, [debouncedUserSearch]);

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const getUserName = useCallback((userId: string): string => {
    const user = userCache.get(userId);
    return user ? user.name : `User ${userId}`;
  }, [userCache]);

  const getTeamName = useCallback((teamId: string): string => {
    return teamCache.get(teamId) ?? `Team ${teamId}`;
  }, [teamCache]);

  const cacheUser = useCallback((user: MondayUser) => {
    setUserCache((prev) => {
      const next = new Map(prev);
      next.set(String(user.id), user);
      return next;
    });
  }, []);

  // ─── Computed: approver entries (read-only) ────────────────────────────────
  const approverEntries = useMemo((): ApproverEntry[] => {
    const seen = new Set<string>();
    const entries: ApproverEntry[] = [];
    for (const r of approveRules) {
      const key = `${r.granteeType}:${r.granteeId}`;
      if (!seen.has(key)) {
        seen.add(key);
        entries.push({
          granteeType: r.granteeType as 'user' | 'team',
          granteeId: r.granteeId,
        });
      }
    }
    return entries.sort((a, b) => {
      const nameA = a.granteeType === 'team' ? getTeamName(a.granteeId) : getUserName(a.granteeId);
      const nameB = b.granteeType === 'team' ? getTeamName(b.granteeId) : getUserName(b.granteeId);
      return nameA.localeCompare(nameB);
    });
  }, [approveRules, getUserName, getTeamName]);

  // ─── Computed: viewer entries (editable) ───────────────────────────────────
  const viewerEntries = useMemo((): ViewerEntry[] => {
    return viewerRules
      .filter((r) => r.granteeType === 'user')
      .map((r) => ({
        ruleId: r.id,
        userId: r.granteeId,
      }))
      .sort((a, b) => getUserName(a.userId).localeCompare(getUserName(b.userId)));
  }, [viewerRules, getUserName]);

  // Set of all approver user IDs (to exclude from viewer dropdown and mark duplicates)
  const approverUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of approverEntries) {
      if (e.granteeType === 'user') ids.add(e.granteeId);
    }
    return ids;
  }, [approverEntries]);

  // Set of existing viewer user IDs
  const viewerUserIds = useMemo(() => {
    return new Set(viewerEntries.map((v) => v.userId));
  }, [viewerEntries]);

  // ─── Dropdown options ──────────────────────────────────────────────────────
  const addViewerOptions = useMemo(() => {
    const fromSearch = userSearchResults
      .filter((u) => !viewerUserIds.has(String(u.id)))
      .map((u) => ({
        value: String(u.id),
        // Mark approvers in the label so admins know the user already has
        // approver access — Viewer grants broader "view all" rights, which is
        // legitimately additive on top of approver-scoped access.
        label: approverUserIds.has(String(u.id))
          ? `${u.name} (${u.email}) — also an approver`
          : `${u.name} (${u.email})`,
      }));
    // Keep selected values in list so chips don't disappear
    const selectedIds = new Set(fromSearch.map((o) => o.value));
    const extras = selectedViewers.filter((s) => !selectedIds.has(s.value));
    return [...extras, ...fromSearch];
  }, [userSearchResults, viewerUserIds, approverUserIds, selectedViewers]);

  // ─── Add viewers ───────────────────────────────────────────────────────────
  const handleAddViewers = useCallback(async () => {
    if (selectedViewers.length === 0) return;
    setIsAddSaving(true);
    setError(null);
    try {
      const payloads = selectedViewers.map((v) => ({
        targetType: 'all' as const,
        targetId: null,
        permission: 'timesheet_view' as const,
        granteeType: 'user' as const,
        granteeId: v.value,
      }));

      let newRules: PermissionRule[];
      if (payloads.length === 1) {
        const rule = await createPermissionRule(payloads[0]);
        newRules = [rule];
      } else {
        newRules = await createPermissionRulesBulk(payloads);
      }

      setViewerRules((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const unique = newRules.filter((r) => !existingIds.has(r.id));
        return [...prev, ...unique];
      });
      setSelectedViewers([]);
      setShowAddForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add viewer');
    } finally {
      setIsAddSaving(false);
    }
  }, [selectedViewers]);

  // ─── Remove viewer ─────────────────────────────────────────────────────────
  const handleRemoveViewer = useCallback(async (ruleId: string) => {
    setDeletingRuleId(ruleId);
    setError(null);
    try {
      await deletePermissionRule(ruleId);
      setViewerRules((prev) => prev.filter((r) => r.id !== ruleId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove viewer');
    } finally {
      setDeletingRuleId(null);
    }
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="rp-permission-rules__loading">
        <Loader size={Loader.sizes.MEDIUM} />
      </div>
    );
  }

  return (
    <div className="rp-timesheet-viewers">
      {/* Error banner */}
      {error && (
        <div className="rp-permission-rules__error-banner">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      )}

      <Text
        type={Text.types.TEXT2}
        style={{ color: '#676879', marginBottom: 20, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}
      >
        Users listed below can view every timesheet in the account, regardless
        of approver/approvee mapping. Approvers who are also added here keep
        their approver rights and gain global view access on top. Admins always
        have full access.
      </Text>

      {/* ── Section 1: Approvers (auto-viewers, read-only) ─────────────── */}
      <div className="rp-timesheet-viewers__section">
        <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
          Approvers
        </Text>
        <Text
          type={Text.types.TEXT2}
          style={{ color: '#676879', marginTop: 4, marginBottom: 12 }}
        >
          These users can view timesheets because they have approver access.
        </Text>

        {approverEntries.length === 0 ? (
          <Text
            type={Text.types.TEXT2}
            style={{ color: '#676879', fontStyle: 'italic' }}
          >
            No approvers configured.
          </Text>
        ) : (
          <div className="rp-timesheet-viewers__viewers-list">
            {approverEntries.map((entry) => (
              <span
                key={`${entry.granteeType}-${entry.granteeId}`}
                className="rp-permission-rules__grantee-chip rp-timesheet-viewers__viewer-chip--disabled"
              >
                <Text type={Text.types.TEXT2}>
                  {entry.granteeType === 'team'
                    ? `Team: ${getTeamName(entry.granteeId)}`
                    : getUserName(entry.granteeId)}
                </Text>
                <span className="rp-timesheet-viewers__approver-badge">
                  Approver
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Section 2: Additional Viewers (editable) ───────────────────── */}
      <div className="rp-timesheet-viewers__section">
        <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
          Additional Viewers
        </Text>
        <Text
          type={Text.types.TEXT2}
          style={{ color: '#676879', marginTop: 4, marginBottom: 12 }}
        >
          Users who can view all timesheets without approver privileges.
        </Text>

        {/* Add viewer form */}
        {showAddForm ? (
          <div className="rp-permission-rules__add-form" style={{ marginBottom: 16 }}>
            <Flex gap={Flex.gaps.SMALL} align={Flex.align.END} wrap>
              <div className="rp-permission-rules__add-field">
                <Text type={Text.types.TEXT2} style={{ marginBottom: 4 }}>
                  User(s) — type 2+ chars to search
                </Text>
                <Dropdown
                  size={Dropdown.sizes.SMALL}
                  multi
                  multiline
                  options={addViewerOptions}
                  value={selectedViewers}
                  onChange={(selected) => {
                    const arr = (selected ?? []) as DropdownOptionObj[];
                    setSelectedViewers(arr);
                    for (const opt of arr) {
                      const user = userSearchResults.find((u) => String(u.id) === opt.value);
                      if (user) cacheUser(user);
                    }
                  }}
                  onOptionRemove={(removedOption) => {
                    if (removedOption) {
                      setSelectedViewers((prev) =>
                        prev.filter((o) => o.value !== (removedOption as DropdownOptionObj).value),
                      );
                    }
                  }}
                  onInputChange={(newValue, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                      setUserSearchQuery(newValue);
                    }
                  }}
                  placeholder="Search users..."
                  isLoading={isUserSearching}
                  closeMenuOnSelect={false}
                  className="rp-permission-rules__dropdown--lg"
                />
              </div>

              <Button
                size={Button.sizes.SMALL}
                onClick={handleAddViewers}
                loading={isAddSaving}
                disabled={selectedViewers.length === 0}
              >
                Add
              </Button>
              <Button
                size={Button.sizes.SMALL}
                kind={Button.kinds.TERTIARY}
                onClick={() => {
                  setShowAddForm(false);
                  setSelectedViewers([]);
                }}
              >
                Cancel
              </Button>
            </Flex>
          </div>
        ) : (
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.TERTIARY}
            leftIcon={Add}
            onClick={() => setShowAddForm(true)}
            style={{ marginBottom: 12 }}
          >
            Add Viewer
          </Button>
        )}

        {/* Existing viewers */}
        {viewerEntries.length === 0 ? (
          <Text
            type={Text.types.TEXT2}
            style={{ color: '#676879', fontStyle: 'italic' }}
          >
            No additional viewers configured.
          </Text>
        ) : (
          <div className="rp-timesheet-viewers__viewers-list">
            {viewerEntries.map((entry) => (
              <span
                key={entry.ruleId}
                className="rp-permission-rules__grantee-chip"
              >
                <Text type={Text.types.TEXT2}>
                  {getUserName(entry.userId)}
                </Text>
                <IconButton
                  icon={CloseSmall}
                  size={IconButton.sizes.XXS}
                  kind={IconButton.kinds.TERTIARY}
                  ariaLabel="Remove viewer"
                  onClick={() => handleRemoveViewer(entry.ruleId)}
                  disabled={deletingRuleId === entry.ruleId}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetViewersTab;
